import { Box, IconButton, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { formatCurrency } from "../../../../utils/formatCurrency";
import { formatDate } from "../../../../utils/dateUtils";
import { useDocumentPdf } from "../../../../api/moloni/useDocumentPdf";
import { InvoiceDocumentsTableProps } from "../types";
import { parseISO, isBefore } from "date-fns";

const getDocumentTypeLabel = (documentTypeId: number) => {
    const documentTypeMap: Record<number, string> = {
        1: "Fatura",
        2: "Recibo",
        3: "Nota de Crédito",
        27: "Fatura/Recibo",
    };
    
    const label = documentTypeMap[documentTypeId];
    return label || "Desconhecido";
};

const getMoloniDocumentUrl = (documentTypeId: number, documentId: number) => {
    const documentPathMap: Record<number, string> = {
        1: "Faturas",
        2: "Recibos",
        3: "NotasCredito",
        27: "FaturasRecibo",
    };
    
    const path = documentPathMap[documentTypeId];
    if (!path) return null;
    
    const url = `https://www.moloni.pt/morningability/${path}/showDetail/${documentId}`;
    return url;
};

type DocumentRowProps = {
    document: any;
    isChild?: boolean;
};

const DocumentRow = ({ document, isChild = false }: DocumentRowProps) => {
    const { mutate: downloadPdf, isLoading: isDownloadingPdf } = useDocumentPdf();

    const handleDownloadPdf = (documentId: number) => {
        downloadPdf(documentId);
    };

    return (
        <TableRow sx={isChild ? { backgroundColor: "#fafafa" } : {}}>
            <TableCell sx={isChild ? { pl: 6 } : {}}>{getDocumentTypeLabel(document.document_type_id)}</TableCell>
            <TableCell>{document.document_set_name}/{document.number}</TableCell>
            <TableCell>{formatDate(document.date)}</TableCell>
            <TableCell align="right">{formatCurrency(document.net_value)}</TableCell>
            <TableCell>{document.notes || "-"}</TableCell>
            <TableCell>
                <IconButton 
                    size="small" 
                    onClick={() => handleDownloadPdf(document.document_id)}
                    disabled={isDownloadingPdf}
                >
                    <PictureAsPdfIcon fontSize="small" />
                </IconButton>
                <IconButton 
                    size="small" 
                    onClick={() => {
                        const moloniUrl = getMoloniDocumentUrl(document.document_type_id, document.document_id);
                        if (moloniUrl) window.open(moloniUrl, "_blank");
                    }}
                >
                    <OpenInNewIcon fontSize="small" />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

export const InvoiceDocumentsTable = ({ invoiceData, isLoading, orderDate }: InvoiceDocumentsTableProps) => {
    const getAllDocuments = () => {
        if (!invoiceData) return [];
        
        const allDocs: any[] = [];
        
        invoiceData.forEach((doc) => {
            allDocs.push({ ...doc, isChild: false });
            if (doc.reverse_associated_documents) {
                doc.reverse_associated_documents.forEach((childDoc) => {
                    allDocs.push({ ...childDoc, isChild: true });
                });
            }
        });
        
        return allDocs;
    };

    const allDocuments = getAllDocuments();

    const cutoffDate = new Date(2025, 10, 15);
    const orderDateParsed = parseISO(orderDate);
    const isBeforeCutoff = isBefore(orderDateParsed, cutoffDate);

    const emptyMessage = isBeforeCutoff
        ? "Apenas são apresentados documentos após 15 de Novembro de 2025"
        : "Ainda não existem gerados para esta compra";

    return (
        <Box mt={3} ml={0.5} p={2} sx={{ backgroundColor: "#fff" }}>
            <Box pt={2} pb={3}>
                <Typography variant="h5">Documentos de Faturação:</Typography>
            </Box>
            {isLoading ? (
                <LinearProgress />
            ) : allDocuments.length > 0 ? (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Tipo de Documento</TableCell>
                                <TableCell>Número</TableCell>
                                <TableCell>Data</TableCell>
                                <TableCell align="right">Total</TableCell>
                                <TableCell>Notas</TableCell>
                                <TableCell width={100} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {allDocuments.map((doc) => (
                                <DocumentRow key={doc.document_id} document={doc} isChild={doc.isChild} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Box py={4} display="flex" justifyContent="center">
                    <Typography color="text.secondary">{emptyMessage}</Typography>
                </Box>
            )}
        </Box>
    );
};

